import { useEffect, useMemo } from 'react';
import {
  selectPostToWalmartFormErrors,
  selectPostToWalmartFormValues,
  setPostToWalmartFormErrors,
} from './slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetWalmartProductData,
  useMutateValidateWalmartFormData,
} from './queries';
import {
  checkProductsSubscriptionShield,
  checkIsGDSNProducts,
  validateProductOwner,
  validateAllActiveProducts,
  validateSubscriptedShield,
  verifyGDSNProductShield,
} from 'common/components/product-publication-setup/utils';

import { useUserInfo } from 'hooks/auth/useUserInfo';

export const useFindSchemaForProduct = ({ productId, walmartSchemas }) => {
  const { productInfos, walmartProductProperties } = walmartSchemas || {};

  const productBrickCodeName = productInfos?.find(
    (productInfoItem) => productInfoItem?.productId === +productId
  )?.brickCodeName;

  if (!productBrickCodeName) return null;

  const foundSchema = walmartProductProperties?.find(
    (schemaItem) => schemaItem?.brickCodeName === productBrickCodeName
  );

  return foundSchema;
};

export const useScrollTopAfterRecursiveRender = (
  refScrollWrapper,
  { schema, visible }
) => {
  useEffect(() => {
    setTimeout(() => {
      if (refScrollWrapper?.current && schema && visible) {
        refScrollWrapper?.current.scrollTo(0, 0);
      }
    }, 300);
  }, [JSON.stringify(schema), visible]);
};

export const useGetFormErrorsByProductId = ({ productId }) => {
  const allFormErrors = useSelector(selectPostToWalmartFormErrors);

  const formErrors = allFormErrors?.find(
    (formErrorsItem) => formErrorsItem?.rivirProductId === productId
  );

  return formErrors;
};

export const useValidateWalmartFormData = () => {
  const walmartFormValues = useSelector(selectPostToWalmartFormValues);

  const mutationValidateWalmartProductData = useMutateValidateWalmartFormData();

  const dispatch = useDispatch();

  const handleValidateWalmartProductData = async (
    walmartFormValuesFormParams
  ) => {
    const sendWalmartForm = walmartFormValuesFormParams || walmartFormValues;

    const payload = {
      productMatchingData: sendWalmartForm,
    };

    const dataValidations =
      await mutationValidateWalmartProductData.mutateAsync(payload);

    if (dataValidations) {
      dispatch(setPostToWalmartFormErrors(dataValidations));
    }

    return dataValidations;
  };

  return {
    handleValidateWalmartProductData,
    isValidatingForm: Boolean(mutationValidateWalmartProductData?.isMutating),
  };
};

export const useGetWalmartFormItemError = ({ productId, errorKey }) => {
  const walmartFormErrors = useSelector(selectPostToWalmartFormErrors);

  let foundFormErrorItem = useMemo(() => {
    if (!walmartFormErrors || !productId) return null;

    const foundFormErrors = walmartFormErrors?.find(
      (walmartFormErrorItem) =>
        walmartFormErrorItem?.rivirProductId === productId
    );

    const foundFormItemErrors = foundFormErrors?.dataErrors?.find(
      (dataErrorItem) => dataErrorItem?.fullKey === errorKey
    );

    return foundFormItemErrors;
  }, [JSON.stringify(walmartFormErrors), productId, errorKey]);

  return foundFormErrorItem;
};

export const useGetWalmartFormErrors = ({ productId }) => {
  const walmartFormErrors = useSelector(selectPostToWalmartFormErrors);

  return useMemo(() => {
    if (!walmartFormErrors || !productId) return null;

    return walmartFormErrors?.find(
      (walmartFormErrorItem) =>
        walmartFormErrorItem?.rivirProductId === productId
    );
  }, [JSON.stringify(walmartFormErrors), productId]);
};

export const useGetInitialWalmartFormValue = ({ productId }) => {
  const { data: initialWalmartProductDataList } = useGetWalmartProductData({
    enabled: false,
    productIds: [+productId],
  });

  return useMemo(() => {
    return initialWalmartProductDataList?.find(
      (initialWalmartFormItem) =>
        initialWalmartFormItem?.productId === +productId
    )?.['data'];
  }, [JSON.stringify(initialWalmartProductDataList)]);
};

export const useCheckAllowToPostGDSN = ({
  isFullView,
  productFull,
  selectedProducts,
}) => {
  const { userInfo } = useUserInfo();

  const checkEnabledGDSNPublishGrid = (products, isRivirGdsnParty) => {
    /*
      Enabled Condition:
      - All owner products
      - All Products active
      - No subscribed products
      - No GDSN products
    */
    if (products?.length === 0) return false;

    const isOwner = validateProductOwner(products);
    const allActiveProducts = validateAllActiveProducts(products);
    const isRIVIRGdsnParty = Boolean(isRivirGdsnParty);
    const hasSubscribedProducts = checkProductsSubscriptionShield(products);
    const hasGDSNProduct = checkIsGDSNProducts(products);

    return (
      isOwner &&
      allActiveProducts &&
      isRIVIRGdsnParty &&
      !hasSubscribedProducts &&
      !hasGDSNProduct
    );
  };

  const checkEnableGDSNPublishFull = (isRivirGdsnParty) => {
    const isOwner = Boolean(productFull?.isOwner);
    const isActive = Boolean(productFull?.status?.toLowerCase() === 'active');
    const isRIVIRGdsnParty = Boolean(isRivirGdsnParty);
    const isSubscribedProduct = validateSubscriptedShield(productFull);
    const isGdsnShield = verifyGDSNProductShield(productFull);

    return (
      isOwner &&
      isActive &&
      isRIVIRGdsnParty &&
      !isSubscribedProduct &&
      !isGdsnShield
    );
  };

  return isFullView
    ? checkEnableGDSNPublishFull(userInfo?.isRivirGdsnParty)
    : checkEnabledGDSNPublishGrid(selectedProducts, userInfo?.isRivirGdsnParty);
};
